"use client"

import type React from "react"

import { useState } from "react"
import { Check, Eye, EyeOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { login, register, sendVerificationCode } from '@/api/auth'
import { getClaimableCount } from '@/api/claim'
import { getDeviceId } from '@/utils/device'

interface LoginModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (hasClaimableShares?: boolean) => void
}

function deriveUsernameFromEmail(email: string): string {
  const prefix = email.split("@")[0] || ""
  const sanitized = prefix.replace(/[^a-zA-Z0-9_]/g, "")
  if (!sanitized) return ""
  if (sanitized.length < 3) {
    return `user_${sanitized}`.slice(0, 30)
  }
  return sanitized.slice(0, 30)
}

export function LoginModal({ open, onClose, onSuccess }: LoginModalProps) {
  const [mode, setMode] = useState<"login" | "register">("login")
  const [email, setEmail] = useState("")
  const [username, setUsername] = useState("")
  const [isUsernameManuallyEdited, setIsUsernameManuallyEdited] = useState(false)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [verificationCode, setVerificationCode] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    
    if (mode === "login") {
      // 登录
      if (!email || !password) {
        setError("请输入邮箱和密码")
        return
      }
      
      setLoading(true)
      try {
        await login({ email, password, remember: true })
        
        // 检查是否有可认领的分享
        const deviceId = getDeviceId()
        const count = await getClaimableCount(deviceId)
        
        onSuccess(count > 0)
      } catch (err: any) {
        setError(err.message || "登录失败")
      } finally {
        setLoading(false)
      }
    } else {
      // 注册
      if (!email || !password || !verificationCode) {
        setError("请填写所有必填项")
        return
      }

      // 如果用户名为空，自动根据邮箱生成
      let finalUsername = username.trim()
      if (!finalUsername) {
        finalUsername = deriveUsernameFromEmail(email)
      }
      if (finalUsername.length < 3) {
        finalUsername = `user_${finalUsername}`.slice(0, 30)
      }
      if (finalUsername.length > 30) {
        setError("用户名长度不能超过30个字符")
        return
      }
      
      if (password.length < 8) {
        setError("密码至少需要8个字符")
        return
      }
      
      if (password !== confirmPassword) {
        setError("两次输入的密码不一致")
        return
      }
      
      // 验证邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        setError("请输入有效的邮箱地址")
        return
      }
      
      // 验证验证码格式（6位数字）
      if (!/^\d{6}$/.test(verificationCode)) {
        setError("验证码必须是6位数字")
        return
      }
      
      setLoading(true)
      try {
        await register({ email, username: finalUsername, password, verificationCode })
        
        // 检查是否有可认领的分享
        const deviceId = getDeviceId()
        const count = await getClaimableCount(deviceId)
        
        onSuccess(count > 0)
      } catch (err: any) {
        setError(err.message || "注册失败")
      } finally {
        setLoading(false)
      }
    }
  }

  const handleEmailChange = (val: string) => {
    setEmail(val)
    if (mode === "register" && !isUsernameManuallyEdited) {
      setUsername(deriveUsernameFromEmail(val))
    }
  }

  const handleUsernameChange = (val: string) => {
    setUsername(val)
    setIsUsernameManuallyEdited(true)
  }

  const switchMode = () => {
    const nextMode = mode === "login" ? "register" : "login"
    setMode(nextMode)
    setError("")
    setVerificationCode("")
    setCountdown(0)
    setIsUsernameManuallyEdited(false)
    if (nextMode === "register" && email) {
      setUsername(deriveUsernameFromEmail(email))
    }
  }

  const handleSendVerificationCode = async () => {
    if (!email) {
      setError("请先输入邮箱地址")
      return
    }
    
    // 验证邮箱格式
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError("请输入有效的邮箱地址")
      return
    }
    
    setSendingCode(true)
    setError("")
    try {
      await sendVerificationCode({ email })
      setError("")
      // 开始倒计时60秒
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err: any) {
      setError(err.message || "发送验证码失败")
    } finally {
      setSendingCode(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <div className="flex flex-col h-full py-8 px-8 justify-center">
          <SheetHeader className="pb-8 pt-16">
            <div className="space-y-3 text-center">
              <p className="text-sm text-gray-500">
                {mode === "login" ? "账号/邮箱登录" : "创建新账号"}
              </p>
              <SheetTitle className="text-3xl font-bold text-gray-900">
                {mode === "login" ? "登录" : "注册"}
              </SheetTitle>
              <SheetDescription className="text-sm text-gray-500 pt-1">
                {mode === "login" 
                  ? "使用邮箱 + 密码登录" 
                  : "填写信息创建账号"}
              </SheetDescription>
            </div>
          </SheetHeader>

          <div className="space-y-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                type="email"
                placeholder="邮箱"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="h-12 rounded-full border-gray-200 px-6 text-base"
                required
              />

              {mode === "register" && (
                <Input
                  type="text"
                  placeholder="用户名（已自动生成，可修改）"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  className="h-12 rounded-full border-gray-200 px-6 text-base"
                />
              )}

              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-full border-gray-200 pl-6 pr-12 text-base"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "隐藏密码" : "显示密码"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {mode === "register" && (
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="确认密码"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 rounded-full border-gray-200 pl-6 pr-12 text-base"
                    required
                  />
                </div>
              )}

              {mode === "register" && (
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="验证码（6位数字）"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 6)
                      setVerificationCode(value)
                    }}
                    className="h-12 rounded-full border-gray-200 px-6 text-base flex-1"
                    maxLength={6}
                    required
                  />
                  <Button
                    type="button"
                    onClick={handleSendVerificationCode}
                    disabled={sendingCode || countdown > 0 || !email}
                    className="h-12 px-6 bg-gray-100 hover:bg-gray-200 disabled:opacity-60 text-gray-700 rounded-full font-medium text-sm whitespace-nowrap"
                  >
                    {sendingCode
                      ? "发送中..."
                      : countdown > 0
                      ? `${countdown}秒后重试`
                      : "发送验证码"}
                  </Button>
                </div>
              )}

              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}

              {mode === "login" && (
                <div className="flex items-center justify-end text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      onClose()
                      window.location.href = '/forgot-password'
                    }}
                    className="text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    忘记密码？
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-black hover:bg-gray-800 disabled:opacity-60 text-white rounded-full font-medium text-base"
              >
                {loading ? (mode === "login" ? "登录中..." : "注册中...") : (mode === "login" ? "登录" : "注册")}
              </Button>

              <div className="text-center">
                <Button 
                  type="button"
                  variant="link" 
                  className="p-0 h-auto text-blue-600"
                  onClick={switchMode}
                >
                  {mode === "login" ? "没有账号？立即注册" : "已有账号？返回登录"}
                </Button>
              </div>
            </form>

            <div className="space-y-5 pt-2">
              <h3 className="text-lg font-bold text-gray-900">
                600万人使用的大文件传输工具
              </h3>

              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 text-gray-600 flex-shrink-0" />
                  <span>不入为速度，资源下载更省力</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 text-gray-600 flex-shrink-0" />
                  <span>无需下载客户端，网页小程序就能在线传输</span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-4 h-4 mt-1 text-gray-600 flex-shrink-0" />
                  <span>专业工作交付，客户免登录就能下载</span>
                </div>
              </div>

              <Button variant="link" className="text-sm text-gray-500 p-0 h-auto hover:text-gray-700">
                了解更多 →
              </Button>
            </div>

            <p className="text-xs text-center text-gray-400 pt-6 leading-relaxed">
              未注册账号自动创建账号为您服务
              <br />
              登录注册即为已同意{" "}
              <a href="#" className="text-blue-600 hover:underline">
                用户协议
              </a>{" "}
              和{" "}
              <a href="#" className="text-blue-600 hover:underline">
                隐私政策
              </a>
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
